import { Injectable } from '@angular/core';
import { FiduciaryRepository } from '@fiduciaria/Shared/Repositories/fiduciary.repository';
import { FileDisplay, FilesMetadataResponse, RequestFiles } from '../Types/files/request-files.interface';
import { Usuario } from '@shared/Entities/Usuario/usuario.entity';
import { UsuariosRepository } from '@shared/Repositories/usuarios.repository';
import { UploadMultipleFilesService } from '@fiduciaria/RequestCreation/Services/upload-multiple-files.service';
import { ModalTitles, ModalType } from '@shared/Constantes/modal.constantes';
import { ModalService } from '@shared/Services/modal.service';
import { catchError, of } from 'rxjs';
import { FiduciaryUtilsService } from './fiduciary-utils.service';

@Injectable({
  providedIn: 'root'
})
export class RequestDetailsDocumentsService  {
  private readonly appendZero = (num: number): string => `0${num}`;
  private readonly files: FileDisplay[];
  public currentUser: Usuario;

  constructor(
    private readonly userRepository: UsuariosRepository, 
    private readonly fiduciaryRepository: FiduciaryRepository,
    private readonly uploadMultipleFilesService: UploadMultipleFilesService,
    private readonly modalService: ModalService,
    private readonly fiduciaryUtilsService: FiduciaryUtilsService
  ) {
    this.files = [];
    this.setCurrentUser(); 
  }

  private setCurrentUser(): void {
    this.userRepository.getInfoClienteActual().subscribe((currentUser: Usuario) => {
      this.currentUser = currentUser;
    });
  }

  public alert(body: string, title: string = ModalTitles.success, type: string = ModalType.success): void {
    this.modalService.showModalGeneral({ title, body, type });
  }

  public base64ToString(str: string): string
  {
    return this.fiduciaryUtilsService.base64StringToString(str);
  }

  public stringToBase64(str: string): string
  {
    return this.fiduciaryUtilsService.stringToBase64String(str);
  }

  public validateRequestNumber(requestNumber: string, isSubCaseNumber: boolean = false): boolean {
      requestNumber = (requestNumber ?? "").trim();
      const regex = (isSubCaseNumber)? /^PG\d+-\d+$/ : /^PG\d+$/;
      return regex.test(requestNumber);
  }

  public timeFormat(date: Date): string {
    let hours = date.getHours();
    const [minutes, seconds] = [date.getMinutes() , date.getSeconds()];
    const time: string = (hours >= 12)? "PM" : "AM";
    hours = hours - 12;

    let template: string = `${(hours >= 10)? hours : this.appendZero(hours)}`;
    template += `:${(minutes >= 10)? minutes : this.appendZero(minutes)}`;
    template += `:${(seconds >= 10)? seconds : this.appendZero(seconds)}`;
    template += ` ${time}`;
    return template;
  }

  public dateFormat(date: Date): string {
    const [day, month, year] = [date.getDate(), (date.getMonth() + 1), date.getFullYear()];
    let template: string = `${(day >= 10)? day : this.appendZero(day) }`;
    template += `/${(month >= 10)? month : this.appendZero(month) }`;
    template += `/${year}`;
    return template;
  }

  public splitDateTime(dateTime: string): string[]
  {
    let dateArray: string[] = dateTime.split(","); 
    let dateParts: number = dateArray.length;
    if(dateParts >= 2) return [dateArray[0], dateArray[1]];
    return [dateTime, ""];
  }

  public isImage(file: File | string): boolean {
    let type: string = (file instanceof File)?  file.type : file;
    return type.includes("image/");
  }
  
  public getSizeMegaBytes(bytes: number, digits: number): number {
    return this.uploadMultipleFilesService.getSizeMegaBytes(bytes, digits);
  }

  public filesToDisplayFiles(files: File[], properties: FileDisplay = {}): FileDisplay[] {
    const result: FileDisplay[] = [];
    for(let file of files) {
      result.unshift({
        name: file.name,
        type: file.type,
        size: {
          megabytes: this.getSizeMegaBytes(file.size, 2),
          bytes: file.size
        },
        extension: file.type.split("/")[1],
        dateModified: new Date(file.lastModified),
        datetime: {
          date: new Date(),
          time: this.timeFormat(new Date())
        },
        creator: {
          guid: this.currentUser.userFbaId,
          name: this.currentUser.nombreCompleto,
          email: this.currentUser.email
        },
        icon: "",
        label: "",
        isNew: true,
        isImage: this.isImage(file),
        url: "",
        file,
        ...properties
      });
    }
    return result;
  }

  public addFiles(files: File[]): FileDisplay[] {
    this.files.unshift(...this.filesToDisplayFiles(files));
    return this.files;
  }

  public uploadFiles(requestFiles: RequestFiles): void {
    const errorMessage: string = "Ocurrió un error al subir los documentos. Por favor, inténtelo de nuevo.";
    if(!requestFiles?.user) requestFiles.user = this.currentUser; 

    this.fiduciaryRepository.subirDocumentos(requestFiles).pipe(
        catchError((error) => {
            this.alert(errorMessage, ModalTitles.danger, ModalType.danger);
            return of(null);
        })
    ).subscribe((data: FilesMetadataResponse) => {
      const [ message, title, type ] = (!data)? [errorMessage, ModalTitles.danger, ModalType.danger]
                                          :["Proceso exitoso!", ModalTitles.success, ModalType.success];
                                          
      this.alert(message, title, type);
    });
  }
}
